import React, { useEffect, useState, useCallback } from "react";
import { Music2, Radio, Volume2 } from "lucide-react";

/**
 * Auto-refreshing "Now Spinning" ticker. Polls the public endpoint
 * every 30 seconds. Falls back gracefully when the stream's offline.
 *
 * Pure-client, no auth, no React Context — safe to drop into any
 * public marketing page.
 */
export default function NowSpinning({
  stationId,
  variant = "card",         // "card" | "ticker" | "minimal"
  className = "",
  pollMs = 30_000,
}) {
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/nowplaying/${stationId}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error("offline");
      setData(await r.json());
    } catch {
      setData((p) => p || { ok: false, error: "Offline" });
    }
  }, [stationId]);

  useEffect(() => {
    load();
    const id = setInterval(() => {
      setTick((t) => t + 1);
      load();
    }, pollMs);
    return () => clearInterval(id);
  }, [load, pollMs]);

  if (!data) return null;
  const track = data.track;
  const isLive = data.ok && track && (track.artist || track.title);

  // ─────────── Ticker variant — single thin row, animated marquee
  if (variant === "ticker") {
    return (
      <div
        className={`flex items-center gap-3 px-4 py-2 rounded-full bg-bg-elevated border border-line text-sm ${className}`}
        data-testid="now-spinning-ticker"
      >
        <span className="relative flex h-2 w-2">
          {isLive && <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />}
          <span className={`relative inline-flex rounded-full h-2 w-2 ${isLive ? "bg-rose-500" : "bg-ink-muted"}`} />
        </span>
        <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">
          {isLive ? "ON AIR" : "OFF AIR"}
        </span>
        {isLive ? (
          <span className="truncate text-ink-primary">
            <span className="text-brand-amber font-semibold">{track.artist}</span>
            {track.title ? <> — {track.title}</> : null}
          </span>
        ) : (
          <span className="text-ink-muted truncate">Station offline</span>
        )}
      </div>
    );
  }

  // ─────────── Minimal — just the line, no styling chrome
  if (variant === "minimal") {
    return (
      <div className={`text-sm ${className}`} data-testid="now-spinning-minimal">
        {isLive ? (
          <>
            <span className="text-ink-muted">Now playing: </span>
            <span className="text-ink-primary"><strong>{track.artist}</strong> — {track.title}</span>
          </>
        ) : (
          <span className="text-ink-muted">Station offline</span>
        )}
      </div>
    );
  }

  // ─────────── Card variant — hero-style block for marketing pages
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-line bg-gradient-to-br from-bg-surface to-bg-elevated p-5 ${className}`}
      data-testid="now-spinning-card"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <span className="relative flex h-2 w-2">
          {isLive && <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />}
          <span className={`relative inline-flex rounded-full h-2 w-2 ${isLive ? "bg-rose-500" : "bg-ink-muted"}`} />
        </span>
        <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-ink-muted">
          {isLive ? "LIVE · NOW SPINNING" : "OFF AIR"}
        </span>
        {isLive && data.listeners != null && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-ink-muted">
            <Volume2 className="h-3 w-3" /> {data.listeners}
          </span>
        )}
      </div>

      {isLive ? (
        <>
          <div className="flex items-start gap-3">
            {track.artwork_url ? (
              <img
                src={track.artwork_url}
                alt={`${track.artist} — ${track.title}`}
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                data-testid="ns-artwork"
                className="h-16 w-16 shrink-0 rounded-lg object-cover ring-1 ring-line shadow-lg"
              />
            ) : (
              <div className="h-12 w-12 shrink-0 rounded-lg bg-brand-amber/10 grid place-items-center">
                <Music2 className="h-6 w-6 text-brand-amber" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-display text-xl font-bold text-ink-primary truncate" data-testid="ns-title">
                {track.title || "Unknown title"}
              </div>
              <div className="text-sm text-brand-amber truncate" data-testid="ns-artist">{track.artist}</div>
            </div>
          </div>
          {data.listen_live_url && (
            <a
              href={data.listen_live_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ns-listen-live"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-amber px-4 py-2 text-sm font-semibold text-black hover:bg-brand-amberHover transition"
            >
              <Radio className="h-4 w-4" /> Listen Live
            </a>
          )}
        </>
      ) : (
        <div className="py-2">
          <div className="text-base text-ink-secondary">Stream is currently offline.</div>
          <div className="text-xs text-ink-muted mt-1">Refreshes automatically when we're back on air.</div>
        </div>
      )}
    </div>
  );
}
